import React, { useMemo } from 'react'
import { GridCell } from '@progress/kendo-react-grid'
import Resx from '@/components/Resx'
import { dialogTypes } from '@/dialogs'
import {
  Column,
  LookupNameColumn,
  DateCell, DateFilterCell, DropDownFilterCell
} from '@/components/BsrsGrid'
import Name from '@/components/Name'

import { PropertyRenderer } from '@/components/changeRequests'
import { useLocalization } from '@/hooks/localization'
import { usePropertyReader } from '@/hooks/usePropertyReader'
import { PermissionSectionProvider } from '@/components/changeRequests/institutionPermissionProvider'

function showDeleteItem (item) {
  return item && item.relatedPersonType && item.relatedPersonType.code !== 'Brnmngr'
}

const createNameColumn = ({ field, alternativeFields, locale, ...props }) => {
  const createPersonNameCell = () => {
    return class PersonNameCell extends GridCell {
      render () {
        const value = this.props.dataItem[field]

        if (value) return <td>{<Name data={value} />}</td>
        let altValue
        for (const alt of alternativeFields) {
          altValue = this.props.dataItem[alt]
          if (altValue) {
            return <td>{altValue}</td>
          }
        }
        return <td>''</td>
      }
    }
  }

  const cellExport = ({ dataItem, field }) => {
    const alternatives = [field, ...alternativeFields]
    let value
    for (const alt of alternatives) {
      value = dataItem[alt]
      if (value) {
        const nameSelector = locale === 'en' ? 'latinName' : 'name'
        return value[nameSelector]
      }
    }
    return ''
  }

  return <Column
    field={field}
    cell={createPersonNameCell()}
    cellExport={cellExport}
    {...props} />
}

const createPersonTypeCell = ({ field, title, localize }) => {
  const data = [
    { id: 1, nameKey: 'Institutions.RelatedIndividual', name: localize('Institutions.RelatedIndividual') },
    { id: 9, nameKey: 'Institutions.RelatedInstitution', name: localize('Institutions.RelatedInstitution') },
    { id: 10, nameKey: 'Institutions.NonFinancialInstitution', name: localize('Institutions.NonFinancialInstitution') }
  ]

  const getKey = (value) => {
    return data.find(x => x.id === value) || { name: '' }
  }

  class PersonTypeCell extends GridCell {
    render () {
      const value = this.props.dataItem[this.props.field]
      const key = getKey(value)
      return <td><Resx name={key.nameKey} /></td>
    }
  }

  const cellExport = ({ dataItem, field }) => {
    const value = dataItem[field]
    const key = getKey(value)
    return localize(key.nameKey)
  }

  return <Column field={field} title={title} cell={PersonTypeCell} cellExport={cellExport}
    filterCell={DropDownFilterCell(data, { dataItemKey: 'id', textField: 'name' })} />
}

export const RelatedPersons = React.memo(function AssociatedToInstitutions (props) {
  return <RelatedPersonsInternal dataName='relatedPersons' {...props} />
})

export const AssociatedToInstitutions = React.memo(function AssociatedToInstitutions (props) {
  return <RelatedPersonsInternal dataName='associatedToInstitutions' {...props} />
})

function RelatedPersonsInternal (props) {
  const { dataSource, readonly, nameField, dataName = 'relatedPersons', permission, currentInstitutionId, activeTab } = props
  const { localize, locale } = useLocalization()
  const [personId] = usePropertyReader(['personId'])
  const detailDialog = useMemo(() => ({ dialogType: dialogTypes.relatedPerson, personId: personId }), [personId])

  const getDetailUrl = ({ relatedInstitutionId }) =>
    relatedInstitutionId !== currentInstitutionId
      ? `/companies/${relatedInstitutionId}?fromCompanyId=${currentInstitutionId}&fromCompanyActiveTab=${activeTab}`
      : ''

  return (
    <PermissionSectionProvider institutionPermission={permission}>
      <PropertyRenderer name={dataName} inputType='grid' dataSource={dataSource} readonly={readonly || false}
        addButtonLabel='Institutions.AddRelatedPerson'
        showGridDeleteButton={showDeleteItem}
        detailDialog={detailDialog}
        detailUrl={getDetailUrl}>
        {createNameColumn({ locale, field: nameField, alternativeFields: ['institutionName', 'companyName'], title: localize('Common.Name'), editable: false })}
        {createPersonTypeCell({ field: 'personType', title: localize('Institutions.PersonType'), localize })}
        {LookupNameColumn({ locale, field: 'relatedPersonType', title: localize('Institutions.RelatedPersonType'), editable: false })}
        <Column field='validFrom' title={localize('Common.ValidFrom')} filter='date' filterCell={DateFilterCell} cell={DateCell} />
        <Column field='validTo' title={localize('Common.DateLeavingPosition')} filter='date' filterCell={DateFilterCell} cell={DateCell} />
      </PropertyRenderer>
    </PermissionSectionProvider>
  )
}



// WEBPACK FOOTER //
// ./src/components/demographics/relatedPersons.js